// Cap Table "Review Mode" store types (SCAL-8976, slice 1).
// reviewItems are derived by the pure aggregator (utilities/reviewAggregator) and
// pushed into the store; the store also tracks selection and the hovered/selected
// cap-table cell (consumed by later slices: hover-to-outline, click-to-jump).

import type { CitationReferenceData, MappingReferenceData } from '../../services/reference-generator/types';

/** A single evidence group rendered as a chip on a review card. */
export interface ReviewEvidenceGroup {
  /** 'citations' | 'aiExtractions' | 'excel' - drives the chip label. */
  kind: ReviewEvidenceKind;
  /** Count to show in the chip; undefined renders the chip without a number. */
  count?: number;
}

export type ReviewEvidenceKind = 'citations' | 'aiExtractions' | 'excel';

/** Status of an AI suggestion, normalized away from the raw is_accepted flag. */
export type ReviewStatus = 'accepted' | 'dismissed' | 'pending';

/** The panel's status VIEW filter (SCAL-9159): everything / pending / dismissed. */
export type ReviewStatusFilter = 'all' | 'pending' | 'dismissed';

/**
 * The panel's provenance VIEW filter. Default 'ai': analysts review AI output,
 * so manual entries are opt-in noise - EXCEPT pending ones, which always show
 * (see reviewItemMatchesFilter). 'manual' and 'all' widen the view.
 */
export type ReviewProvenanceFilter = 'ai' | 'manual' | 'all';

/** Identifies a cap-table cell by its security (columnId) and field (alias). */
export interface ReviewCellRef {
  securityId: number;
  fieldAlias: string;
}

/** True when `ref` addresses the same cell as `target` (security + field). */
export const cellRefMatches = (ref: ReviewCellRef | null, target: ReviewCellRef): boolean =>
  ref != null && ref.securityId === target.securityId && ref.fieldAlias === target.fieldAlias;

/**
 * True when `item` passes the panel filter. Each dimension is a set of selected
 * labels; an EMPTY set means "no constraint on that dimension". The two dimensions
 * combine with AND (security AND metric), so picking "Series A" + "Shares
 * Outstanding" shows only that intersection. The status VIEW filter (SCAL-9159)
 * ANDs on top: 'all' passes every status, 'pending'/'dismissed' pass only that
 * status. Applied uniformly to pending + reviewed items so the filter behaves the
 * same in every section.
 */
export const reviewItemMatchesFilter = (
  item: ReviewItem,
  securities: Set<string>,
  metrics: Set<string>,
  status: ReviewStatusFilter = 'all',
  provenance: ReviewProvenanceFilter = 'all',
  cellIds?: Set<string>
): boolean =>
  (status === 'all' || item.status === status) &&
  // Provenance composes with AND - but PENDING items always pass it: pending
  // review is pending review, the provenance view must never hide actionable
  // items (e.g. rollover-created manual citations awaiting review in the
  // default AI view).
  (provenance === 'all' || item.status === 'pending' || (provenance === 'ai') === item.aiCreated) &&
  (securities.size === 0 || securities.has(item.securityLabel)) &&
  (metrics.size === 0 || metrics.has(item.fieldLabel)) &&
  (!cellIds || cellIds.size === 0 || cellIds.has(item.id));

/** One review card's worth of data, keyed by (securityId, fieldAlias). */
export interface ReviewItem {
  /** Stable id: `${securityId}:${fieldAlias}`. */
  id: string;
  securityId: number;
  fieldAlias: string;
  /**
   * Review state of the item. The aggregator emits EVERY evidenced cell (pending +
   * accepted + dismissed; AI-suggested AND analyst-cited - SCAL-9159) so the panel
   * can partition pending cards (top) from reviewed cards (bottom). On accept/decline
   * the store flips this in place so the card MOVES from pending to reviewed instead
   * of vanishing.
   */
  status: ReviewStatus;
  /**
   * Provenance (SCAL-9159): true for AI-created items (an AI field suggestion, or a
   * citation-only item whose active citation is AI-created per isAiCreatedCitation),
   * false for analyst/manual entries. Drives the card's AI/Manual chip.
   */
  aiCreated: boolean;
  /**
   * Whether accept/decline can settle this item at the FIELD level: true only for
   * cells carrying an aiFieldMetadata entry (the accept/dismiss-ai-fields and
   * ai-review-batch endpoints operate on field metadata). Drives the thumbs AND
   * the bulk checkbox.
   */
  canSettle: boolean;
  /**
   * Whether accept/decline can settle this item at the CITATION level (SCAL-8963):
   * true for reference-only items (no aiFieldMetadata) whose ACTIVE citation can be
   * reviewed through PATCH /api/cap-tables/citations/<id>/review. Renders the same
   * thumbs but is deliberately EXCLUDED from bulk/select-all - the batch endpoint
   * settles field metadata only.
   */
  canSettleCitation?: boolean;
  /** Whether accept/decline settles this item's AI Excel MAPPING rows (SCAL-9667). */
  canSettleMapping?: boolean;
  /** Every AI Excel mapping id on the cell - the batch target of a mapping settle. */
  mappingIds?: number[];
  /**
   * How accepting the suggestion would change the cell's data, at a glance:
   * 'merge' = a previous value exists and DIFFERS from the suggestion (accept
   * supersedes real data - the AIMergeValueViewer prev -> ai case), 'new' = the
   * cell is empty and the suggestion fills it. Undefined for citation-only
   * items (no field value change) and for suggestions equal to the current
   * value (nothing is superseded or filled). Drives the card's Merge/New chip.
   */
  valueChange?: 'merge' | 'new';
  /**
   * True when the item was auto-settled as a DUPLICATE of an already-accepted
   * fact (backend echo-suggestion dedupe: review_reason='duplicate' on the
   * field metadata entry or on the active citation). Renders a muted
   * "Duplicate" chip on the settled card.
   */
  duplicate?: boolean;
  /**
   * SCAL-9262: set when un-accepting KEPT the cell's value because it differed
   * from what the accept wrote (a manual edit made after acceptance is never
   * clobbered). Transient UI state stamped by the reopen flow (a full
   * re-aggregation drops it); renders a "Value kept" chip on the reopened card.
   */
  valueKept?: boolean;
  /** Security column header, e.g. "Class A". */
  securityLabel: string;
  /** Human field label, e.g. "Original Issue Price". */
  fieldLabel: string;
  /** The security's column position in the grid, so lists can follow the grid's column order. */
  columnPosition?: number;
  /** The field's row position in the grid, so lists can follow the grid's row order. */
  rowPosition?: number;
  /** Displayed (suggested) value as a string, e.g. "$1.00". */
  displayedValue: string;
  /**
   * The citation's cited text, filling the headline when there is no suggested
   * value to show. Derived only from the citation evidence - never from the
   * live cell - so a settled card cannot change after an accept or reject.
   * Rendered distinctly from the suggested-value line: it is evidence, not a
   * proposed change.
   */
  citedValue?: string;
  /**
   * Composed card title: "securityLabel - fieldLabel - displayedValue", with the
   * trailing value segment dropped when it equals the security label (so the
   * Security-Name field reads "Series D - Security Name", not "... - Series D").
   */
  title: string;
  /** Evidence chips with counts (only groups with count > 0 are included). */
  evidence: ReviewEvidenceGroup[];
  /** AI commentary / verification notes for the expandable detail. */
  commentary: string[];
  /** AI field confidence (0..1) if known. */
  confidence?: number | null;
  /** Quoted evidence snippets from citations, for the expandable detail. */
  citationTexts: string[];
  /** Uploaded filename for the persisted field evidence quote. */
  citationDocumentName?: string;
  /** Source document name(s) backing this suggestion. */
  documentNames: string[];
  /**
   * PRIMARY (first) document-backed citation on the cell, if any. Slice 3 uses it to
   * jump the drawer's document view to the source and highlight the cited tokens.
   * Undefined for AI-extraction-only or Excel-mapping-only items (no doc jump).
   */
  primaryCitation?: CitationReferenceData;
  /**
   * Every citation row on the cell, primary included: settle lookups and the
   * evidence-only marker's provenance read the full set.
   */
  citations?: CitationReferenceData[];
  /**
   * PRIMARY (first) Excel mapping reference on the cell, if any. Feature C uses it to
   * open the xlsx viewer to the mapped sheet and highlight the mapped cell(s).
   * Undefined for citation-only / AI-extraction-only items (no Excel jump).
   */
  primaryMappingReference?: MappingReferenceData;
}

export interface ReviewStore {
  reviewItems: ReviewItem[];
  selectedCardIds: Set<string>;
  /**
   * Active panel filter (SCAL-9153), split into two dimensions of selected labels.
   * An empty set means "show every label" for that dimension; the two dimensions
   * combine with AND. Filtering hides non-matching cards in EVERY section (pending,
   * accepted, dismissed) so "select all" only ever sees the visible set.
   */
  filterSecurities: Set<string>;
  filterMetrics: Set<string>;
  /**
   * Per-cell filter from a grid multi-select (SCAL-9183). Empty means no
   * cell-id constraint. Combined with AND on the other filter dimensions.
   */
  filterCellIds: Set<string>;

  /**
   * Status VIEW filter (SCAL-9159): 'all' | 'pending' | 'dismissed'. A separate
   * always-visible control (not part of the security/metric funnel menu) that ANDs
   * with the label filters above.
   */
  filterStatus: ReviewStatusFilter;
  /**
   * Provenance VIEW filter: 'ai' (default) | 'manual' | 'all'. ANDs with the
   * status/label filters; pending items always pass it.
   */
  filterProvenance: ReviewProvenanceFilter;
  hoveredReviewCell: ReviewCellRef | null;
  selectedReviewCell: ReviewCellRef | null;
  /**
   * Whether the cap-table bottom drawer (review panel) is open. Drawer-open IS the
   * review context: while true, pending AI cells in the grid flag themselves AI-purple
   * by default (SCAL-9150). The ReviewSidePanel - which only mounts while the drawer is
   * open - drives this flag, so closing the drawer reverts cells to their normal colors.
   */
  drawerOpen: boolean;
  /**
   * Whether the user hid the review panel with the pending-review pill; null until
   * they toggle it, in which case the panel is hidden only while it has no cards.
   */
  paneCollapsed: boolean | null;
  /**
   * Feature toggle (SCAL-9155, default OFF) for the in-grid AI column UI: the
   * dashed-line border on AI-generated columns AND the per-column accept/dismiss
   * pill controls. This UI was retired in favor of the review drawer's bulk
   * actions, so it is OFF by default but gated (not deleted) so it can be
   * re-enabled. The drawer's bulk accept/dismiss is unaffected by this flag.
   */
  showAiColumnControls: boolean;
  /**
   * Role gate for the whole AI review experience (SCAL-9335). True only for
   * analyst-side users (staff/superuser, or an analyst/firm-admin/fund-admin
   * firm role) - stamped by CapTableWidgetDrawer from useAiReviewerRole. While
   * false (the safe default), grid viewers render the PLAIN cap table: normal
   * blue citation badges, no AI hue or preview, no settle affordances.
   */
  reviewerMode: boolean;

  /** Replace the derived review items (called by the aggregator wiring). */
  setReviewItems: (items: ReviewItem[]) => void;
  /** Toggle a card's selection membership in selectedCardIds. */
  toggleCardSelection: (id: string) => void;
  /** Replace the selection with EXACTLY these ids (used by select-all-visible). */
  selectCards: (ids: string[]) => void;
  /** Clear all card selections. */
  clearSelection: () => void;
  /** Toggle a security label in the panel filter. */
  toggleFilterSecurity: (label: string) => void;
  /** Toggle a metric (field label) in the panel filter. */
  toggleFilterMetric: (label: string) => void;
  /** Clear both filter dimensions (back to showing everything). */
  clearReviewFilter: () => void;
  /** Restrict the panel to these review-item ids (grid selection filter). */
  setFilterCellIds: (ids: string[]) => void;
  /** Switch the status view filter (all / pending / dismissed). */
  setFilterStatus: (status: ReviewStatusFilter) => void;
  /** Switch the provenance view filter (ai / manual / all). */
  setFilterProvenance: (provenance: ReviewProvenanceFilter) => void;
  /** Optimistically drop an item once accept/decline succeeds. */
  removeReviewItem: (id: string) => void;
  /**
   * Flip an item's status in place (and deselect it) so it MOVES between the
   * pending and reviewed sections on accept/decline/reopen, rather than being
   * removed. The next full re-aggregation reconciles the same shape from the
   * cells. `valueKept` (SCAL-9262) stamps the reopened card's "Value kept" flag;
   * omitting it clears any stale flag on the next transition.
   */
  setReviewItemStatus: (id: string, status: ReviewStatus, valueKept?: boolean) => void;
  /** Set the cap-table cell currently hovered (consumed by later slices). */
  setHoveredReviewCell: (cell: ReviewCellRef | null) => void;
  /** Set the cap-table cell currently selected (consumed by later slices). */
  setSelectedReviewCell: (cell: ReviewCellRef | null) => void;
  /** Set whether the review drawer is open (gates the pending-cell AI-purple state). */
  setDrawerOpen: (open: boolean) => void;
  /** Collapse/expand the review side panel (driven by the pending-review badge). */
  setPaneCollapsed: (collapsed: boolean) => void;
  /** Toggle the in-grid AI column UI (dashed border + per-column pill controls). */
  setShowAiColumnControls: (show: boolean) => void;
  /** Stamp whether the current user gets the AI review experience (SCAL-9335). */
  setReviewerMode: (on: boolean) => void;
  /**
   * Drop every piece of review state that belongs to one cap table sheet: items,
   * card selection, the filter dimensions, the pane flag and the reviewer stamp.
   * The drawer runs it on unmount so the next table starts clean.
   */
  resetReviewSession: () => void;
}
